import { Component } from '@angular/core';
import { OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../../services/alerts.service';
import { UserService } from '../../../services/user.service';
import { ConfigService } from '../../../services/config.service';
import { ProductService } from '../../../services/product.service';
import { BrowseService } from '../../services/browse.service';
import { SearchService } from '../../../search/services/search.service';

import cloneDeep from 'lodash/cloneDeep';

// For typeahead functionality
import { Observable, OperatorFunction, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

/**
 * Component for editing browse path metadata values.
 */
@Component({
	selector: 'browse-edit',
	templateUrl: './browse-edit.component.html',
	styleUrl: './browse-edit.component.css'
})
export class BrowseEditComponent implements OnInit {
	@Input() productGroup: any; // productGroup.records = all records in the current results page, productGroup.ids = selected prods
	@Input() browsepath!: any;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	isEditMode: boolean = true;
	browsedata: { [key: string]: { ids: any; deleted: boolean } } = {};
	data: any[] = [];

	copysteps: any[] = [];
	copypath: string = "";
	pageMetadataEdit: boolean = false;

	inprogress: boolean = false;

	constructor(
		private configService: ConfigService,
		private userService: UserService,
		private alertsService: AlertsService,
		private browseService: BrowseService,
		private productService: ProductService,
		private searchService: SearchService,
		private ngbmodalService: NgbActiveModal
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();

		this.isEditMode = this.productGroup.ids.length > 1;

		let selected_lib_name = this.getSelectedLibrary();
		this.buildBrowseData(selected_lib_name);
	}

	/**
	 * Determines if all products in the selected group originate 
	 * from the same library. If so, returns the source library name, otherwise undefined.
	 */
	private getSelectedLibrary(): string | undefined {
		let doc = this.productGroup.records.find((record: any) => { return this.productGroup.ids.length > 0 && record.id == this.productGroup.ids[0]; });
		let selected_lib_name = doc ? doc.library : this.browsepath ? this.browsepath.lib_name : undefined;
		if (this.productGroup.ids.length > 1) {
			for (let i = 1; i < this.productGroup.ids.length; i++) {
				doc = this.productGroup.records.find((record: any) => { return record.id == this.productGroup.ids[i]; });
				if (doc.library != selected_lib_name) {
					return undefined;
				}
			}
		}
		return selected_lib_name;
	}

	private buildBrowseData(lib?: string): void {
		if (!lib && this.isEditMode) {
			this.alertsService.error({
				title: "Multiple Libraries Detected",
				message: "The record filer cannot be used with documents from multiple libraries.",
				delay: 8000
			});
			this.close();
		}

		// Build the existing browsepath tags
		this.browsedata = {};
		if (this.browsepath) {
			let paths = Array.isArray(this.browsepath.path) ? this.browsepath.path : [this.browsepath.path];
			for (const path of paths) {
				if (path.length > 0) {
					this.browsedata[path] = { ids: this.productGroup.ids, deleted: false };
				}
			}
		}
		else {
			this.browseService.getBrowsePathsForProducts(this.productGroup.ids).then(
				(response: any) => {
					this.browsedata = {};
					Object.entries(response).forEach(([path, ids]) => {
						this.browsedata[path] = { ids: ids, deleted: false };
					});
				},
				(err: any) => {
					console.error("Failed to get product browse paths", err);
					this.alertsService.error({
						message: "Failed to retrieve document metadata."
					});
				}
			);
		}


		// Build the input fields
		let library = this.userService.user.properties.awlibraryObjectList.find((libraryObj: any) => {
			return libraryObj.name == lib || libraryObj.displayName == lib;
		});

		if (library && library.awhierarchyObject) {
			let suggestFields = this.configService.getConfig().search.autoSuggest.enabled ? this.configService.getConfig().search.autoSuggest.fields : [];

			for (const lib of library.awhierarchyObject) {
				let field = this.userService.user?.searchInputs?.find((field: any) => { return field.name == lib.hierarchyName; });

				if (field) {
					let browseObj = {
						'name': lib.hierarchyName,
						'type': field.options && field.options.length > 0 ? "list" : "string",
						'options': field.options || [],
						'filteredOptions': field.options || [],
						'displayName': field.title,
						'value': '',
						'defaultValue': '',
						//'required': this.options.required[name],
						'dependents': field.dependents,
						'filter': field.filter,
						'input': true,
						'suggest': false
					};

					let systemField = this.userService.user?.systemPreferences?.find((field: any) => { return field.name == browseObj.name; });
					if (systemField.defaultValue) {
						browseObj["value"] = systemField.defaultValue;
						browseObj["defaultValue"] = systemField.defaultValue;
					}

					for (const suggest of suggestFields) {
						if (field.name == suggest) {
							browseObj["suggest"] = true;
							break;
						}
					}

					this.data.push(browseObj);
				}
				else {
					console.error(`Failed to find field ${lib.hierarchyName} in user's search inputs list.`);
				}
			}
		}
	}

	// NOTE: This component operates on a separate field object, and not directly on userService.user.searchInputs
	// >> Dependent select fields
	private isEmpty(formItem: any): boolean {
		return !formItem.value;
	}

	/**
	  * Applies filtering logic based on the parentMask value.
	  */
	applyFilter(formItem: any) {
		if (!formItem?.options) {
			formItem.filteredOptions = [];
			return;
		}

		if (formItem.filter) {
			formItem.filteredOptions = formItem.options.filter((option: any) =>
				this.filterComparator(option.parentMask, formItem.filter?.parentMask)
			);
		} else {
			// Show all options if no filter
			formItem.filteredOptions = [...formItem.options];
		}
		
		if (formItem.filteredOptions.length == 1) {
			formItem.value = formItem.filteredOptions[0].value;
		}
	}

	/**
	 * Custom filter function to compare values.
	 */
	filterComparator(actual: string, expected: string): boolean {
		if (!actual || !expected) return false;

		let parts = actual.split(" OR ");
		return parts.some((part: string) => part.toLowerCase() === expected.toLowerCase());
	}

	/**
	 * Handles field updates and adjusts dependents accordingly.
	 */
	updateFn(formItem: any) {
		if (!formItem || !formItem.dependents) return;

		let validDeps: { [key: string]: any } = {};

		if (formItem.options) {
			for (let option of formItem.options) {
				if (option.value === formItem.value && option.childs) {
					for (let child of option.childs) {
						validDeps[child.name] = child;
					}
				}
			}
		}

		Object.values(formItem.dependents).forEach((dependent: any) => {
			let dependentName = dependent?.name;
			let dependentItem = this.copysteps.find((x: any) => x.name === dependentName) || this.data.find((x: any) => x.name === dependentName);

			if (dependentItem) {
				dependentItem.filter = !validDeps[dependentItem.name] || this.isEmpty(formItem)
					? null
					: { parentMask: formItem.value };
			}
			this.applyFilter(dependentItem);
		});
	}
	// << END Dependent select fields  


	initializeInlineEdit(path?: string): void {
		this.copysteps = [];
		
		if (path) {
			this.pageMetadataEdit = true;

			let steps = [];
			this.copypath = path;
			let paths = this.copypath.split(this.browseService.pathSeparator);
			for (const path of paths) {
				let browseField = path.split(/:(.*)/);
				steps.push({ name: browseField[0], value: browseField[1] });
			}

			for (const field of this.data) {
				let currentStep;
				for (const step of steps) {
					if (field.name === step.name) {
						currentStep = step;
						break;
					}
				}
				let fieldvalue = currentStep ? currentStep.value : undefined;
				let copystep = cloneDeep(field);
				copystep.value = fieldvalue; 	
				
				// Apply any dependent value filters
				if (copystep.type === 'list') {
					this.updateFn(copystep);
				}
				
				this.copysteps.push(copystep);
			}
		} else {
			this.pageMetadataEdit = false;
		}
	}

	saveInlineEdit(): void {
		this.pageMetadataEdit = false;
		let newpath = '';
		let newpathvals = [];

		for (const step of this.copysteps) {
			if (step.value != "" && step.value != null) {
				newpathvals.push(step.name + ":" + step.value);
			}
		}
		if (newpathvals.length > 0) {
			newpath = newpathvals.join(this.browseService.pathSeparator);
		}

		let updates = {} as any;
		Object.entries(this.browsedata).forEach(([path, ids]) => {
			if (path === this.copypath) {
				updates[newpath] = ids;
			} else {
				updates[path] = ids;
			}
		});
		this.browsedata = updates;
	}

	removePath(path: string): void {
		let keys = Object.keys(this.browsedata);
		for (const key of keys) {
			if (key === path) {
				this.browsedata[key]['deleted'] = true;
				break;
			}
		}
	}

	/**
	 * Creates a new browsepath from any filled inputs.
	 */
	addPath(): void {
		let newpathvals = [];
		for (let i = 0; i < this.data.length; i++) {
			let field = this.data[i];
			if (field.value != "" && field.value != null) {
				newpathvals.push(field.name + ":" + field.value);
			}
			this.data[i].value = this.data[i].defaultValue || "";
		}
		if (newpathvals.length > 0) {
			let newpath = newpathvals.join(this.browseService.pathSeparator);
			this.browsedata[newpath] = { ids: this.productGroup.ids, deleted: false };
		} else {
			this.alertsService.error({
				title: "Missing Values",
				message: "Enter at least one value to add a new browse path."
			});
		}
	}

	/**
	 * Submitting the form will update the browsepath metadata field of the selected documents.
	 */
	update(): void {
		// Display for prototype and debugging only:
		// console.log("Edit Result");
		// console.log( JSON.stringify(this.browsedata, null, '\t') );

		//
		// Package a payload per set of prod ids all having the same paths
		//
		// (1) For each prod id, collect an array of paths
		if (this.browsepath) {
			this.proceed();
		}
		else {
			let prodtopaths: { [key: string]: any } = {};

			Object.entries(this.browsedata).forEach(([path, data]) => {
				for (const pid of data.ids) {
					if (prodtopaths.hasOwnProperty(pid)) {
						if (!data.deleted) {
							prodtopaths[pid].push(path);
						}
					} else {
						if (!data.deleted) {
							prodtopaths[pid] = [path];
						} else {
							prodtopaths[pid] = [];
						}
					}
				}
			});
			//            console.log("prodtopaths:");
			//            let clone = JSON.parse(JSON.stringify(prodtopaths));
			//            console.log(clone);


			let sameMembers = function(arr1: any[], arr2: any[]) {
				const set1 = new Set(arr1);
				const set2 = new Set(arr2);
				return arr1.every(item => set2.has(item)) &&
					arr2.every(item => set1.has(item));
			};

			let findSamePaths = function(prodtopaths: any, patharr: any[], payload: any) {
				for (const pid in prodtopaths) {
					if (prodtopaths.hasOwnProperty(pid)) {
						let patharrtocheck = prodtopaths[pid];
						if (sameMembers(patharr, patharrtocheck)) {
							payload.ids.push(pid);
							delete prodtopaths[pid];
						}
					}
				}
			};

			// (2) Group prod ids together that have the same paths
			let payloads = [];
			for (const pid in prodtopaths) {
				if (prodtopaths.hasOwnProperty(pid)) {
					let payload: { [key: string]: any } = {};
					payload['replaceAll'] = false;
					payload['replaceFieldValues'] = true;

					let paths = prodtopaths[pid];
					payload['metadata'] = { 'browsepath': paths };
					payload['ids'] = [pid];
					delete prodtopaths[pid];

					findSamePaths(prodtopaths, paths, payload);
					payloads.push(payload);
				}
			}
			//            console.log("payloads:");
			//            console.log(payloads);
			//
			// END payload package processing
			//

			// Iterate for each payload, collect responses in turn. Operation is not atomic.
			this.inprogress = true;
			let errors = [];
			let successes = [];
			let promises = [];
			for (const payload of payloads) {
				promises.push(this.productService.massUpdateMetadata(payload).then(
					(response: any) => {
						successes.push(response);
					},
					(err: any) => {
						console.error("Error updating product metadata.", err);
						errors.push(err);
					}
				));
			}

			Promise.all(promises).then(
				(response: any) => {
					if (errors.length > 0 && successes.length == 0) {
						this.alertsService.error({
							title: "Update Failed",
							message: "Failed to process the browsepath changes."
						});
					} else if (errors.length > 0 && successes.length > 0) {
						//let ids = [];
						//for (let i = 0; i < errors.length; i++) {
						//	if (errors[i].ids) { ids.push(...errors[i].ids); }
						//}
						//console.error("Failed to process the browsepath changes for some documents. Potentially affects document IDs: " + ids.join());
						this.alertsService.error({
							title: "Partial Update",
							message: "Failed to process the browsepath changes for some documents."
						});
					}
					else {
						this.alertsService.success({
							title: "Successfully Updated",
							message: "The browsepath changes have been processed. Please wait for the index to propagate the change.",
							delay: 5000
						});
					}
					this.inprogress = false;
					this.close();
				}
			);
		}
	}


	// Converts the service Promise to an Observable emitting a string array
	private getSuggestions(fieldid: string, val: string): Observable<string[]> {
		// searchService returns a Promise
		return from(this.searchService.getTermSuggestions(fieldid, val)) as Observable<string[]>;
	}

	/**
	 * Typeahead search handler.
	 */
	search = (text$: Observable<string>) =>
		text$.pipe(
			debounceTime(200),
			distinctUntilChanged(),
			switchMap((term) => {
				// Get the input element that triggered the search
				const inputElement = document.activeElement as HTMLInputElement;
				const fieldName = inputElement?.getAttribute('data-fieldname') || 'Unknown_Field';

				console.log('Received term:', term, 'for field:', fieldName);

				return from(this.getSuggestions(fieldName, term));
			})
		);

	/**
	 * Closes the modal, returning a result of ok and payload.
	 */
	proceed(): void {
		let caller = {
			callerName: 'editBrowse',
			data: Object.keys(this.browsedata) ?? {}
		};

		this.ngbmodalService.close({
			result: { answer: 'ok', caller: caller },
			timestamp: new Date(),
		});
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}
